import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BetaMembershipBanner from '../components/BetaMembershipBanner';
import {
  PawPrint,
  Mail,
  Calendar,
  Users,
  MessageSquare,
  Bell,
  Pill,
  Stethoscope,
  Heart,
  ChevronRight,
  Sparkles,
  Clock,
  AlertCircle,
  Plus,
  TrendingUp,
  Newspaper
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MemberDashboard - Personalized homepage for logged-in members
 */
const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [hotDiscussions, setHotDiscussions] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [newMembers, setNewMembers] = useState([]);
  const [petTypeNews, setPetTypeNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all dashboard data in parallel
      const [
        statsRes,
        remindersRes,
        announcementsRes,
        discussionsRes,
        eventsRes,
        membersRes,
        newsRes
      ] = await Promise.all([
        fetch(`${API}/api/dashboard/stats`, { 
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API}/api/dashboard/reminders?limit=5`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API}/api/announcements/latest?limit=3`),
        fetch(`${API}/api/dashboard/hot-discussions?limit=5`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API}/api/dashboard/upcoming-events?limit=5`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API}/api/dashboard/new-members?limit=5`),
        fetch(`${API}/api/dashboard/pet-type-news?limit=5`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (remindersRes.ok) setReminders(await remindersRes.json());
      if (announcementsRes.ok) setAnnouncements(await announcementsRes.json());
      if (discussionsRes.ok) setHotDiscussions(await discussionsRes.json());
      if (eventsRes.ok) setUpcomingEvents(await eventsRes.json());
      if (membersRes.ok) setNewMembers(await membersRes.json());
      if (newsRes.ok) setPetTypeNews(await newsRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReminderIcon = (type) => {
    switch (type) {
      case 'vet_appointment': return <Stethoscope className="w-4 h-4" />;
      case 'medicine': return <Pill className="w-4 h-4" />;
      case 'playdate': return <Heart className="w-4 h-4" />;
      case 'event': return <Calendar className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getReminderColor = (type, isOverdue) => {
    if (isOverdue) return 'text-red-500 bg-red-50';
    switch (type) {
      case 'vet_appointment': return 'text-blue-500 bg-blue-50';
      case 'medicine': return 'text-purple-500 bg-purple-50';
      case 'playdate': return 'text-pink-500 bg-pink-50';
      case 'event': return 'text-green-500 bg-green-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700',
      event: 'bg-blue-100 text-blue-700',
      policy: 'bg-purple-100 text-purple-700',
      welcome: 'bg-green-100 text-green-700',
      community: 'bg-amber-100 text-amber-700',
      feature: 'bg-cyan-100 text-cyan-700',
      general: 'bg-gray-100 text-gray-700'
    };
    return colors[category] || colors.general;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="member-dashboard">
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-medium">
            Welcome back, {user?.display_name?.split(' ')[0] || user?.username}!
          </h1>
          <p className="text-muted-foreground">Here's what's happening in your community</p>
        </div>

        {/* Beta Membership Banner */}
        {user?.is_beta_user && (
          <div className="mb-8">
            <BetaMembershipBanner />
          </div>
        )}

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/my-pets')}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <PawPrint className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pets_count}</p>
                  <p className="text-xs text-muted-foreground">My Pets</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/messages')}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.unread_messages}</p>
                  <p className="text-xs text-muted-foreground">Messages</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/events')}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.upcoming_events}</p>
                  <p className="text-xs text-muted-foreground">Events</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/friends')}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending_friend_requests}</p>
                  <p className="text-xs text-muted-foreground">Requests</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/feed')}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.new_forum_replies}</p>
                  <p className="text-xs text-muted-foreground">Replies</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Reminders & Alerts */}
            {reminders.length > 0 && (
              <Card data-testid="reminders-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Bell className="w-5 h-5 text-primary" />
                      Reminders & Alerts
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/reminders')}>
                      View All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reminders.map((reminder) => (
                    <div 
                      key={reminder.id}
                      className={`flex items-center gap-3 p-3 rounded-lg ${reminder.is_overdue ? 'bg-red-50 border border-red-200' : 'bg-muted/50'}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getReminderColor(reminder.type, reminder.is_overdue)}`}>
                        {getReminderIcon(reminder.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{reminder.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {reminder.is_overdue ? (
                            <span className="text-red-500 font-medium">Overdue</span>
                          ) : (
                            format(new Date(reminder.due_date), 'MMM d, h:mm a')
                          )}
                          {reminder.pet_name && ` • ${reminder.pet_name}`}
                        </p>
                      </div>
                      {reminder.is_overdue && (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Club News & Announcements */}
            <Card data-testid="announcements-section">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Newspaper className="w-5 h-5 text-primary" />
                    Club News
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {announcements.length > 0 ? (
                  <div className="space-y-3">
                    {announcements.map((announcement) => (
                      <div 
                        key={announcement.id}
                        className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start gap-2 mb-1">
                          <Badge className={getCategoryColor(announcement.category)} variant="secondary">
                            {announcement.category}
                          </Badge>
                          {announcement.is_pinned && (
                            <Badge variant="outline" className="text-xs">Pinned</Badge>
                          )}
                        </div>
                        <h4 className="font-medium text-sm">{announcement.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {announcement.content}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No announcements yet
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Content Tabs: Hot Discussions, Pet News, Feed */}
            <Card>
              <Tabs defaultValue="discussions">
                <CardHeader className="pb-0">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="discussions" className="text-sm">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      Hot Discussions
                    </TabsTrigger>
                    <TabsTrigger value="pet-news" className="text-sm">
                      <PawPrint className="w-4 h-4 mr-1" />
                      For Your Pets
                    </TabsTrigger>
                    <TabsTrigger value="feed" className="text-sm">
                      <Sparkles className="w-4 h-4 mr-1" />
                      Feed
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>

                <CardContent className="pt-4">
                  {/* Hot Discussions Tab */}
                  <TabsContent value="discussions" className="mt-0">
                    {hotDiscussions.length > 0 ? (
                      <div className="space-y-2">
                        {hotDiscussions.map((post) => (
                          <div 
                            key={post.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/post/${post.id}`)}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{post.title}</p>
                              <p className="text-xs text-muted-foreground">
                                @{post.author_username} • {post.comments_count} comments
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No hot discussions right now
                      </p>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full mt-3"
                      onClick={() => navigate('/forums')}
                    >
                      Browse All Forums
                    </Button>
                  </TabsContent>

                  {/* Pet Type News Tab */}
                  <TabsContent value="pet-news" className="mt-0">
                    {petTypeNews.length > 0 ? (
                      <div className="space-y-2">
                        {petTypeNews.map((post) => (
                          <div 
                            key={post.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/post/${post.id}`)}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{post.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {post.content}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No news for your pet types yet
                      </p>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full mt-3"
                      onClick={() => navigate('/feed')}
                    >
                      View Full Feed
                    </Button>
                  </TabsContent>

                  {/* Feed Tab - Quick link */}
                  <TabsContent value="feed" className="mt-0">
                    <div className="text-center py-6">
                      <p className="text-muted-foreground mb-4">
                        See all posts from your community
                      </p>
                      <Button onClick={() => navigate('/feed')}>
                        <Plus className="w-4 h-4 mr-2" />
                        Go to Feed
                      </Button>
                    </div>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Upcoming Events */}
            <Card data-testid="upcoming-events-section">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    Upcoming Events
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {upcomingEvents.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingEvents.map((event) => (
                      <div 
                        key={event.id}
                        className="p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/events`)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{event.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(event.date), 'MMM d, h:mm a')}
                            </p>
                          </div>
                          {event.is_registered && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                              Going
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No upcoming events
                  </p>
                )}
                <Button 
                  variant="outline" 
                  className="w-full mt-3"
                  onClick={() => navigate('/events')}
                >
                  Browse Events
                </Button>
              </CardContent>
            </Card>

            {/* New Members */}
            <Card data-testid="new-members-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-600" />
                  New Members
                </CardTitle>
              </CardHeader>
              <CardContent>
                {newMembers.length > 0 ? (
                  <div className="space-y-3">
                    {newMembers.map((member) => (
                      <div 
                        key={member.id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                        onClick={() => navigate(`/profile/${member.username}`)}
                      >
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={member.avatar_url} />
                          <AvatarFallback>{member.display_name?.charAt(0) || member.username?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{member.display_name || member.username}</p>
                          <p className="text-xs text-muted-foreground">
                            Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No new members this week
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/my-pets')}>
                  <PawPrint className="w-4 h-4 mr-2" />
                  Add a Pet
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/feed')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Post
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/events')}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Find Events
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/marketplace')}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Marketplace
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
